import { Button } from "@/components/flexkit/Button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { toast } from "@/components/podkit/toasts/use-toast";
import { type PlainPrompt } from "@/queries/prompt-queries";
import { formatError } from "@/utils/errors";
import { MoreHorizontal } from "lucide-react";
import { memo, useCallback, useState, type FC } from "react";
import { EditSkillModal } from "@/routes/settings/organization/agents/EditSkillModal";
import { DeleteSkillModal } from "@/routes/settings/organization/agents/DeleteSkillModal";
import { SkillCreatedAt } from "@/routes/settings/organization/agents/SkillCreatedAt";
import { SkillCreatedBy } from "@/routes/settings/organization/agents/SkillCreatedBy";

type Props = {
    skill: PlainPrompt;
};

const AgentSkillCardComponent: FC<Props> = ({ skill }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const onCopyTrigger = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(`/${skill.spec?.command || ""}`);
            toast({
                title: "Trigger copied to clipboard",
                description: `/${skill.spec?.command}`,
            });
        } catch (error) {
            toast({
                title: "Failed to copy trigger to clipboard",
                description: formatError(error),
            });
        }
    }, [skill.spec?.command]);

    const onEdit = useCallback(() => {
        setShowEditModal(true);
    }, []);

    const onDelete = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    return (
        <>
            <div
                data-testid={`skill-card-${skill.id}`}
                className={cn(
                    "group flex flex-col gap-4 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-3 text-left transition-all duration-200",
                    "hover:border-border-medium hover:shadow-md",
                )}
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <h3 className="truncate text-lg font-semibold leading-tight text-content-primary">
                            {skill.metadata?.name}
                        </h3>
                        {skill.spec?.command && (
                            <div className="mb-4 font-mono text-sm text-content-secondary">/{skill.spec.command}</div>
                        )}
                        <p className="line-clamp-2 text-sm text-content-secondary">{skill.metadata?.description}</p>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`skill-actions-${skill.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={onCopyTrigger}
                                data-tracking-id="copy-agent-skill-agent-skill-card"
                            >
                                Copy Trigger
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={onEdit} data-tracking-id="edit-agent-skill-agent-skill-card">
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={onDelete}
                                className="text-content-red"
                                data-tracking-id="delete-agent-skill-agent-skill-card"
                            >
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="mt-auto flex flex-col">
                    <SkillCreatedAt skill={skill} />
                    <SkillCreatedBy skill={skill} />
                </div>
            </div>

            {showEditModal && <EditSkillModal skill={skill} onClose={() => setShowEditModal(false)} />}
            {showDeleteModal && <DeleteSkillModal skill={skill} onClose={() => setShowDeleteModal(false)} />}
        </>
    );
};

export const AgentSkillCard = memo(AgentSkillCardComponent);
